// ProgressBar.js
import React, { useState, useEffect } from "react";
import { Clock } from "lucide-react";
import { formatProgressEtaCopy } from "./analysisProgress";
import "./ProgressBar.css";

const ProgressBar = ({
  processed,
  total,
  showEstimate = true,
  compact = false,
  stage,
}) => {
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [startTime] = useState(Date.now());
  const [lastProcessed, setLastProcessed] = useState(processed);
  const [averageTimePerFile, setAverageTimePerFile] = useState(1000); // Default 1 second per file

  const percentage = total > 0 ? Math.floor((processed / total) * 100) : 0;

  useEffect(() => {
    if (processed > lastProcessed && processed > 0) {
      // Calculate the actual time taken per file based on elapsed time and files processed
      const elapsedTime = Date.now() - startTime;
      const newAvgTime = elapsedTime / processed;

      // Use a weighted average to smooth out the estimate (70% old value, 30% new value)
      setAverageTimePerFile((prev) => 0.7 * prev + 0.3 * newAvgTime);
      setLastProcessed(processed);
    }

    // Calculate remaining time
    if (processed < total) {
      const filesRemaining = total - processed;
      const msRemaining = filesRemaining * averageTimePerFile;
      setTimeRemaining(msRemaining);
    } else {
      setTimeRemaining(0);
    }
  }, [processed, total, lastProcessed, startTime, averageTimePerFile]);

  const resolvedStage =
    stage ||
    (total > 0 && processed >= total ? "finalizing" : "analyzing_files");

  return (
    <div className={`progress-container ${compact ? "compact" : ""}`}>
      <div className="progress-bar-wrapper">
        <div
          className="progress-bar-fill"
          style={{ width: `${percentage}%` }}
          role="progressbar"
          aria-valuenow={percentage}
          aria-valuemin="0"
          aria-valuemax="100"
        />
      </div>
      <div className="progress-details">
        <span className="progress-text">
          {processed} / {total} files ({percentage}%)
        </span>
        {showEstimate && (
          <span className="time-estimate">
            <Clock size={compact ? 12 : 16} />
            {formatProgressEtaCopy(timeRemaining, { stage: resolvedStage })}
          </span>
        )}
      </div>
    </div>
  );
};

export default ProgressBar;
